import { Link } from "@/lib/router-compat";
import { ArrowRight, MapPin, Camera, Eye, Layers, MonitorSmartphone, Building2, Home } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import MagneticButton from "@/components/MagneticButton";
import Testimonials from "@/components/Testimonials";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const usages = [
  { icon: Home, title: "Particuliers en travaux", desc: "Voir sa cuisine avec le plan de travail clair, puis avec le foncé, avant de signer le devis du cuisiniste. C'est souvent là que les discussions de couple se règlent." },
  { icon: Building2, title: "Restaurateurs et hôteliers", desc: "Un dossier de financement passe plus facilement avec trois vues de la salle qu'avec un plan au format A3." },
  { icon: MonitorSmartphone, title: "Promoteurs et agences", desc: "Vendre un lot avant la livraison, montrer une hauteur sous plafond et une orientation que le plan de vente ne raconte pas." },
  { icon: Layers, title: "Concours et appels d'offres", desc: "Des perspectives calées sur vos fichiers CAO, livrées au format demandé par le règlement du concours." },
];

const atouts = [
  { icon: Eye, title: "Lumière juste", desc: "J'utilise l'orientation réelle du bâtiment et l'heure que vous choisissez. Une pièce plein nord ne sera pas rendue comme une terrasse à Marseille." },
  { icon: Camera, title: "Plusieurs angles", desc: "Perspectives intérieures, vues depuis l'entrée, coupes, vues aériennes. On cadre là où le regard tombe vraiment quand on pousse la porte." },
  { icon: Layers, title: "Variantes", desc: "Deux ou trois ambiances sur la même scène : parquet contre carrelage, mur sombre contre mur clair. Les modifications se font en quelques jours." },
];

interface FeaturedProject {
  id: string; title: string; slug: string; cover_image: string | null;
}

const Images3DVisualisationArchitecturale = () => {
  const [projects, setProjects] = useState<FeaturedProject[]>([]);

  useEffect(() => {
    supabase.from("projects").select("id, title, slug, cover_image")
      .eq("published", true)
      .eq("category", "Infographie 3D")
      .order("display_order")
      .then(({ data }) => setProjects(data ?? []));
  }, []);

  return (
    <Layout>
      <SEOHead
        title="Images 3D & Visualisation architecturale Lyon | JIIA Studio"
        description="Rendus 3D photoréalistes pour vos projets d'architecture à Lyon. Perspectives intérieures, extérieures, vues aériennes. JIIA Studio, infographiste 3D en Rhône-Alpes."
        canonical="/images-3d-visualisation-architecturale"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Images 3D & Visualisation architecturale",
            "provider": { "@type": "LocalBusiness", "name": "JIIA Studio", "url": "https://jiia-studio.com" },
            "areaServed": [{ "@type": "City", "name": "Lyon" }, { "@type": "AdministrativeArea", "name": "Rhône-Alpes" }],
            "serviceType": "Visualisation architecturale 3D",
            "description": "Création d'images 3D photoréalistes pour projets architecturaux : résidentiel, hôtellerie, immobilier, concours."
          }
        ]}
      />

      {/* Hero */}
      <section className="py-24 md:py-32 bg-secondary/30">
        <div className="container">
          <AnimatedSection>
            <div className="flex items-center gap-2 text-sm text-muted-foreground font-body mb-4">
              <Camera className="h-4 w-4" />
              <span>Infographie 3D · Lyon · Rhône-Alpes · France</span>
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-light leading-tight mb-6">
              Images 3D<br />
              <span className="italic">& visualisation architecturale</span>
            </h1>
            <p className="text-lg text-muted-foreground font-body max-w-2xl leading-relaxed mb-8">
              Un plan se lit mal quand on n'a pas l'habitude. Une image, elle, se comprend en deux secondes,
              et c'est souvent elle qui débloque une décision : la teinte du sol, la place de l'îlot,
              l'accord d'un banquier. Je modélise vos pièces à partir de vos plans ou de mes relevés,
              pour des logements comme pour des projets d'hôtellerie et de concours.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <MagneticButton>
                  <span className="flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-sm font-body text-sm uppercase tracking-widest hover:bg-primary/90 transition-colors">
                    Demander un devis <ArrowRight className="h-4 w-4" />
                  </span>
                </MagneticButton>
              </Link>
              <Link to="/projets">
                <MagneticButton>
                  <span className="flex items-center gap-2 px-8 py-4 border border-border rounded-sm font-body text-sm uppercase tracking-widest hover:bg-secondary transition-colors">
                    Voir le portfolio 3D
                  </span>
                </MagneticButton>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Atouts */}
      <section className="py-20 md:py-28">
        <div className="container">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Qualité</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-12">
              Ce que je soigne dans un rendu
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {atouts.map((a, i) => (
              <AnimatedSection key={a.title} delay={i * 0.15}>
                <div className="text-center p-8">
                  <a.icon className="h-10 w-10 text-accent mx-auto mb-4" />
                  <h3 className="font-heading text-xl mb-3">{a.title}</h3>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">{a.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Usages */}
      <section className="py-20 md:py-28 bg-secondary/30">
        <div className="container">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Pour qui ?</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-12">
              À qui s'adressent les images 3D ?
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {usages.map((u, i) => (
              <AnimatedSection key={u.title} delay={i * 0.1}>
                <div className="flex gap-4 p-6 border border-border rounded-sm hover:border-accent/50 transition-colors">
                  <u.icon className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading text-lg mb-2">{u.title}</h3>
                    <p className="text-sm text-muted-foreground font-body leading-relaxed">{u.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio */}
      {projects.length > 0 && (
        <section className="py-20 md:py-28">
          <div className="container">
            <AnimatedSection>
              <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Portfolio</p>
              <h2 className="font-heading text-3xl md:text-4xl font-light mb-12">
                Exemples de visualisations 3D
              </h2>
            </AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project, i) => (
                <AnimatedSection key={project.id} delay={i * 0.1}>
                  <Link to={`/projets/${project.slug}`} className="group block">
                    <div className="aspect-[4/3] overflow-hidden rounded-sm mb-3">
                      <img src={project.cover_image || ""} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    </div>
                    <h3 className="font-heading text-base group-hover:text-accent transition-colors">{project.title}</h3>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      <Testimonials />

      {/* CTA */}
      <section className="py-20 md:py-28 bg-primary text-primary-foreground">
        <div className="container text-center">
          <AnimatedSection>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-4">
              Vous avez des plans à faire parler ?
            </h2>
            <p className="text-primary-foreground/70 font-body max-w-lg mx-auto mb-8 leading-relaxed">
              Envoyez vos plans, même au crayon, avec le nombre de vues souhaité. Je vous réponds sous 48h avec un devis et un délai.
            </p>
            <Link to="/contact">
              <MagneticButton>
                <span className="inline-flex items-center gap-2 px-10 py-4 bg-rose text-white rounded-sm font-body text-sm uppercase tracking-widest hover:bg-rose/90 transition-colors">
                  Demander un devis 3D <ArrowRight className="h-4 w-4" />
                </span>
              </MagneticButton>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
};

export default Images3DVisualisationArchitecturale;
